import { AnyConferenceChangedByOtherEvent, BaseEvent, HistoryMessageItem, MessageCallReceived, MessageIncomingEvent, MessagePinnedByOtherEvent, MessageUnPinnedByOtherEvent, PServed } from '@wui/im'
import { computed, onBeforeUnmount, onMounted, onUpdated, ref, watch, WatchHandle } from 'vue'
import { useRoute } from 'vue-router'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { useStoreLastReadEventIds } from '@/entities/room'
import { useWebRTC } from '@/entities/webrtc'

import { fromPServedUser, fromPServedUserIfRoom, isPServedIsUser } from '@/shared/utils/uri'

type MessageItemWithUnreadState =
  | MessageIncomingEvent
  | MessageCallReceived
  | AnyConferenceChangedByOtherEvent
  | MessagePinnedByOtherEvent
  | MessageUnPinnedByOtherEvent

export const useUnreadMessages = (event: HistoryMessageItem<MessageItemWithUnreadState>) => {
  const { getMarkedUnread, getOwnMessageRankByRoomAndContactId } = useStoreLastReadEventIds()

  const unreadMark = computed(() => getMarkedUnread(event.item.pServed))

  const _isUnread = computed(() => {
    const { pServed, rank } = event.item
    if (unreadMark.value && unreadMark.value.rank <= rank) {
      return true
    } else {
      const lastOwnReadRank = getOwnMessageRankByRoomAndContactId(pServed) ?? 0
      return rank > lastOwnReadRank
    }
  })
  const isUnread = ref(_isUnread.value)

  let watchHandle: WatchHandle
  let markUnreadWatchHandle: WatchHandle

  const start = () => {
    if (isUnread.value) {
      watchHandle = watch(_isUnread, () => {
        setTimeout(() => {
          isUnread.value = _isUnread.value
        }, 500)
      })
    }

    markUnreadWatchHandle = watch(unreadMark, () => {
      isUnread.value = _isUnread.value
    })
  }

  const stop = () => {
    watchHandle?.stop()
    markUnreadWatchHandle?.stop()
  }

  onMounted(() => start())
  onBeforeUnmount(() => stop())
  onUpdated(() => {
    stop()
    start()
  })

  return {
    isUnread,
  }
}

const hasUnreadState = (
  event: HistoryMessageItem<BaseEvent>,
): event is HistoryMessageItem<MessageItemWithUnreadState> => {
  return 'rank' in event.item && 'pServed' in event.item && 'eventId' in event.item
}

const buildPossibleIds = (pServed: PServed): Set<string> => {
  const ids = new Set<string>([pServed, fromPServedUserIfRoom(pServed)])
  if (isPServedIsUser(pServed)) {
    ids.add(fromPServedUser(pServed))
  }
  return ids
}

const isNotCurrentRoute = (pServed: PServed): boolean => {
  const route = useRoute()
  if (route?.name !== 'Chat' && route?.name !== 'CallSession') {
    return true
  }

  const possibleIds = buildPossibleIds(pServed)

  if (route.name === 'CallSession') {
    const sessionIdParam = route.params.sessionId
    const sessionId = Array.isArray(sessionIdParam) ? sessionIdParam[0] : sessionIdParam
    if (typeof sessionId !== 'string') {
      return true
    }
    const session = useWebRTC().sessions.value.get(sessionId)
    const callRoomPServed = session ? useCallSessionRoomStore().getRoom(session.sessionId)?.pServedUser : undefined
    if (!callRoomPServed) {
      return true
    }
    const currentCallPossibleIds = buildPossibleIds(callRoomPServed)
    const isViewingThisChat = [...possibleIds].some((id) => currentCallPossibleIds.has(id))
    return !isViewingThisChat
  }

  const routeParam = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id
  if (typeof routeParam !== 'string') {
    return true
  }

  let decodedRouteId: string
  try {
    decodedRouteId = atob(routeParam)
  } catch (error) {
    console.warn('Failed to decode chat route id', error)
    return true
  }

  return !possibleIds.has(decodedRouteId)
}

export const useSafeUnreadState = (event: HistoryMessageItem<BaseEvent>) => {
  if (hasUnreadState(event) && !isNotCurrentRoute(event.item.pServed)) {
    return useUnreadMessages(event)
  }

  return { isUnread: ref(false) }
}
